import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// Same prop API as the shared ModalX (components/modal-x.js) so it's a drop-in look-alike,
// but with the fade+pop-in animation and inv- design language. Scoped to this module only —
// ModalX itself (used app-wide) is left untouched.
const MaintenanceModal = ({
    isOpen,
    onClose,
    title,
    children,
    maxWidth = 'md', // sm, md, lg, xl, xxl, full
    showCloseButton = true,
}) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const maxWidthClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-full md:max-w-[70%]',
        xxl: 'max-w-full lg:max-w-[70%] xl:max-w-[50%]',
        full: 'max-w-full',
    };

    return createPortal(
        <div className="fixed inset-0 overflow-y-auto overflow-x-hidden" style={{ zIndex: 2000 }}>
            <div
                className="fixed inset-0 bg-inv-ink/40 backdrop-blur-sm animate-invFadeIn"
                onClick={onClose}
            />

            <div className="flex min-h-screen items-center justify-center p-4 md:p-5">
                <div
                    className={`relative w-full ${maxWidthClasses[maxWidth]} rounded-2xl bg-white shadow-2xl animate-invPopIn`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {(title || showCloseButton) && (
                        <div className="flex items-center justify-between border-b border-inv-cream-200 px-6 py-4">
                            {title && typeof title === 'string' && (
                                <span className="text-lg font-extrabold text-inv-ink">{title}</span>
                            )}
                            {title && typeof title === 'object' && title}
                            {showCloseButton && (
                                <button
                                    onClick={onClose}
                                    className="rounded-full p-1.5 text-[#8C8175] transition-colors hover:bg-inv-cream-100 hover:text-inv-ink"
                                >
                                    <X size={20} />
                                </button>
                            )}
                        </div>
                    )}

                    <div className="max-h-[75vh] overflow-y-auto custom-scrollbar px-6 py-5">
                        {children}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default MaintenanceModal;
